import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Link,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import axiosInstance from "../utlis/axiosInstance";

const ForgotPassword: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Form state
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Validation state
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  // Step management
  const [activeStep, setActiveStep] = useState(0);
  const [otpSent, setOtpSent] = useState(false);

  const validateEmail = (email: string) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!email) {
      setEmailError("Email is required");
      return false;
    } else if (!re.test(email.toLowerCase())) {
      setEmailError("Invalid email address");
      return false;
    } else {
      setEmailError("");
      return true;
    }
  };

  const validatePhone = (phone: string) => {
    if (!phone) {
      setPhoneError("Phone number is required");
      return false;
    } else if (phone.length < 10) {
      setPhoneError("Phone number must be at least 10 digits");
      return false;
    } else {
      setPhoneError("");
      return true;
    }
  };

  const validateOtp = (otp: string) => {
    if (!otp) {
      setOtpError("OTP is required");
      return false;
    } else if (otp.length !== 4) {
      setOtpError("OTP must be 4 digits");
      return false;
    } else {
      setOtpError("");
      return true;
    }
  };

  const validatePassword = (password: string) => {
    if (!password) {
      setPasswordError("Password is required");
      return false;
    } else if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return false;
    } else {
      setPasswordError("");
      return true;
    }
  };

  const validateConfirmPassword = (password: string, confirmPassword: string) => {
    if (!confirmPassword) {
      setConfirmPasswordError("Please confirm your password");
      return false;
    } else if (password !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      return false;
    } else {
      setConfirmPasswordError("");
      return true;
    }
  };

  const handleSendOtp = async () => {
    const isEmailValid = validateEmail(email);
    const isPhoneValid = validatePhone(phone);

    if (!isEmailValid && !isPhoneValid) {
      setError("Please provide either email or phone number");
      return;
    }

    if (!isEmailValid && !isPhoneValid) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const payload: any = {};
      if (email) payload.email = email;
      if (phone) payload.phone = phone;

      await axiosInstance.post("/gpstracker/forgotpasswordhandler/", payload);
      
      setSuccess("OTP sent successfully! Please check your email/phone.");
      setOtpSent(true);
      setActiveStep(1);
    } catch (err: any) {
      console.error("Send OTP error:", err);
      setError(err.response?.data?.message || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    const isOtpValid = validateOtp(otp);
    const isPasswordValid = validatePassword(newPassword);
    const isConfirmPasswordValid = validateConfirmPassword(newPassword, confirmPassword);

    if (!isOtpValid || !isPasswordValid || !isConfirmPasswordValid) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const payload: any = {
        otp_code: otp,
        new_password: newPassword,
      };
      
      if (email) payload.email = email;
      if (phone) payload.phone = phone;

      await axiosInstance.put("/gpstracker/forgotpasswordhandler/", payload);
      
      setSuccess("Password reset successfully! You can now login with your new password.");
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err: any) {
      console.error("Reset password error:", err);
      setError(err.response?.data?.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  const steps = ["Send OTP", "Reset Password"];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: { xs: 2, sm: 4 },
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={isMobile ? 3 : 0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 2,
            bgcolor: "background.paper",
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <img
                src="/logo.png"
                alt="Logo"
                style={{
                  width: "50px",
                  height: "50px",
                }}
              />
            </div>

            <Typography variant="h4" component="h1" gutterBottom>
              Forgot Password
            </Typography>

            <Typography variant="body1" color="textSecondary">
              Reset your password to access your account
            </Typography>
          </Box>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Back to Login Button */}
          <Box sx={{ mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToLogin}
              sx={{ color: "text.secondary" }}
            >
              Back to Login
            </Button>
          </Box>

          {/* Error and Success Messages */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {/* Step 1: Send OTP */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Enter your email or phone number
              </Typography>
              
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                We'll send you a verification code to reset your password
              </Typography>

              <TextField
                margin="normal"
                fullWidth
                id="email"
                label="Email Address (Optional)"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => validateEmail(email)}
                error={!!emailError}
                helperText={emailError}
                sx={{ mb: 2 }}
              />

              <TextField
                margin="normal"
                fullWidth
                id="phone"
                label="Phone Number (Optional)"
                name="phone"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onBlur={() => validatePhone(phone)}
                error={!!phoneError}
                helperText={phoneError}
                sx={{ mb: 3 }}
              />

              <Button
                fullWidth
                variant="contained"
                onClick={handleSendOtp}
                disabled={loading || (!email && !phone)}
                sx={{
                  py: 1.5,
                  fontWeight: "bold",
                  bgcolor: "var(--primary-color)",
                  "&:hover": {
                    bgcolor: "var(--primary-light)",
                  },
                }}
              >
                {loading ? <CircularProgress size={24} /> : "Send OTP"}
              </Button>
            </Box>
          )}

          {/* Step 2: Reset Password */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Enter OTP and new password
              </Typography>
              
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Enter the 4-digit code sent to your {email ? 'email' : 'phone'} and create a new password
              </Typography>

              <TextField
                margin="normal"
                fullWidth
                id="otp"
                label="OTP Code"
                name="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                onBlur={() => validateOtp(otp)}
                error={!!otpError}
                helperText={otpError}
                inputProps={{ maxLength: 4 }}
                sx={{ mb: 2 }}
              />

              <TextField
                margin="normal"
                fullWidth
                name="newPassword"
                label="New Password"
                type={showPassword ? "text" : "password"}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onBlur={() => validatePassword(newPassword)}
                error={!!passwordError}
                helperText={passwordError}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <Button
                      onClick={() => setShowPassword(!showPassword)}
                      size="small"
                      sx={{ minWidth: "auto" }}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </Button>
                  ),
                }}
              />

              <TextField
                margin="normal"
                fullWidth
                name="confirmPassword"
                label="Confirm New Password"
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => validateConfirmPassword(newPassword, confirmPassword)}
                error={!!confirmPasswordError}
                helperText={confirmPasswordError}
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <Button
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      size="small"
                      sx={{ minWidth: "auto" }}
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </Button>
                  ),
                }}
              />

              <Button
                fullWidth
                variant="contained"
                onClick={handleResetPassword}
                disabled={loading}
                sx={{
                  py: 1.5,
                  fontWeight: "bold",
                  bgcolor: "var(--primary-color)",
                  "&:hover": {
                    bgcolor: "var(--primary-light)",
                  },
                }}
              >
                {loading ? <CircularProgress size={24} /> : "Reset Password"}
              </Button>

              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Button
                  onClick={() => {
                    setActiveStep(0);
                    setOtp("");
                    setNewPassword("");
                    setConfirmPassword("");
                    setError(null);
                    setSuccess(null);
                  }}
                  sx={{ color: "text.secondary" }}
                >
                  Resend OTP
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ForgotPassword; 